import React, { useEffect, useState, useCallback } from 'react';
import useBackdropDismiss from '@/hooks/useBackdropDismiss';
import { Eye, EyeOff, Copy, RefreshCw, AlertCircle, Check, Save } from 'lucide-react';
import { api } from '@/lib/api';
import API_URL from '@/lib/apiConfig';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';

const EMPTY_TOKEN_PLACEHOLDER = '••••••••-••••-••••-••••-••••••••••••';

export default function SettingsTab() {
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('ai_dispatcher.settings.update');
  const canRotate = hasPermission('ai_dispatcher.settings.rotate_token');

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Token reveal / copy state. `revealedToken` holds the plaintext ONLY after
  // an explicit reveal — GET /settings returns a masked value and nothing
  // else, so there is no full token in this component's state (or in the
  // page's network log) until the operator asks for it. Same permission gate
  // as rotation, enforced server-side.
  const [revealedToken, setRevealedToken] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Rotate confirmation
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  // Workflow URL
  const [workflowUrl, setWorkflowUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);

  const closeRotate = useCallback(() => setRotateConfirmOpen(false), []);
  const backdrop = useBackdropDismiss(closeRotate);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${API_URL}/api/ai-dispatcher/settings`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to load settings');
      setSettings(data);
      setRevealedToken(null);
      setWorkflowUrl(data?.linked_workflow_url ?? '');
    } catch (err) {
      setError(err?.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Fetches the plaintext from the audited reveal endpoint. Toggling off just
  // drops it from state so a left-open Settings tab isn't holding the secret.
  const handleToggleReveal = async () => {
    if (revealedToken) {
      setRevealedToken(null);
      return;
    }
    setRevealing(true);
    try {
      const res = await api.post(`${API_URL}/api/ai-dispatcher/settings/reveal-token`, {});
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to reveal token');
      setRevealedToken(data.n8n_token);
    } catch (err) {
      toast.error(err?.message || 'Failed to reveal token');
    } finally {
      setRevealing(false);
    }
  };

  const handleCopy = async () => {
    // Copy needs the real value, so reveal first if it isn't in hand yet —
    // otherwise the operator silently pastes a row of bullets into n8n.
    let token = revealedToken;
    if (!token) {
      try {
        const res = await api.post(`${API_URL}/api/ai-dispatcher/settings/reveal-token`, {});
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || 'Failed to read token');
        token = data.n8n_token;
      } catch (err) {
        toast.error(err?.message || 'Failed to read token');
        return;
      }
    }
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      toast.success('Token copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy — please copy manually after revealing');
    }
  };

  const handleRotate = async () => {
    setRotating(true);
    try {
      const res = await api.post(`${API_URL}/api/ai-dispatcher/settings/rotate-token`, {});
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to rotate token');
      // Rotation returns the new plaintext once. Still shown immediately —
      // it is the only time the value is returned unmasked — but as of
      // Channels contract v1.3 it is no longer something the operator must
      // go and paste anywhere: BOSS reads this value per relay and supplies
      // it to the workflow in the payload.
      setRevealedToken(data.n8n_token);
      setRotateConfirmOpen(false);
      toast.success('Token rotated. It takes effect on the next workflow run.');
    } catch (err) {
      toast.error(err?.message || 'Failed to rotate token');
    } finally {
      setRotating(false);
    }
  };

  const handleSaveUrl = async () => {
    if (!canUpdate) return;
    setSavingUrl(true);
    try {
      const res = await api.patch(`${API_URL}/api/ai-dispatcher/settings`, {
        linked_workflow_url: workflowUrl,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to save');
      toast.success('Workflow URL saved');
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSavingUrl(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-4 flex items-start gap-3 max-w-xl">
        <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-boss-text">{error}</p>
          <button onClick={fetchSettings} className="text-xs text-violet-400 hover:text-violet-300 mt-1">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Masked form comes from the server now; the browser never receives the
  // other 32 characters unless the operator explicitly reveals.
  const displayToken =
    revealedToken || settings?.n8n_token_masked || EMPTY_TOKEN_PLACEHOLDER;

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-lg font-semibold text-boss-text">Dispatcher Settings</h2>
        <p className="text-sm text-boss-text-secondary mt-0.5">
          Integration credentials and workflow configuration.
        </p>
      </div>

      {/* n8n Token */}
      <div className="glass-card p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-boss-text mb-1">n8n Workflow Token</h3>
          <p className="text-xs text-boss-text-muted">
            Used by your n8n workflow to authenticate dispatch requests. Keep this secret.
          </p>
        </div>

        {/* Token display */}
        <div className="flex items-center gap-2">
          <div
            className="flex-1 bg-boss-elevated border border-boss-border rounded-lg px-3 py-2.5 font-mono text-sm text-boss-text truncate select-all min-w-0"
            aria-label="n8n token"
            title={revealedToken || undefined}
          >
            {displayToken}
          </div>

          {/* Reveal + copy both need the plaintext, which only the
              reveal endpoint hands out (admin + rotate_token). Hidden for
              anyone who can't call it rather than left there to 403. */}
          {canRotate && (
            <>
              <button
                onClick={handleToggleReveal}
                disabled={revealing}
                className="p-2.5 rounded-lg border border-boss-border bg-boss-elevated hover:bg-boss-surface transition-colors text-boss-text-secondary hover:text-boss-text disabled:opacity-40 shrink-0"
                title={revealedToken ? 'Hide token' : 'Reveal token'}
                aria-label={revealedToken ? 'Hide token' : 'Reveal token'}
              >
                {revealedToken ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>

              <button
                onClick={handleCopy}
                className="p-2.5 rounded-lg border border-boss-border bg-boss-elevated hover:bg-boss-surface transition-colors text-boss-text-secondary hover:text-boss-text disabled:opacity-40 shrink-0"
                title="Copy token to clipboard"
                aria-label="Copy token to clipboard"
              >
                {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
              </button>
            </>
          )}
        </div>

        {!canRotate && (
          <p className="text-xs text-boss-text-muted">
            Only an admin with the rotate-token permission can reveal or copy this
            value. The last 4 characters are shown so you can confirm which token
            n8n is configured with.
          </p>
        )}

        {/* Rotate */}
        {canRotate && (
          <div>
            <button
              onClick={() => setRotateConfirmOpen(true)}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <RefreshCw size={15} />
              Regenerate Token
            </button>
            <p className="text-xs text-boss-text-muted mt-1.5">
              Rotating the token immediately invalidates the old one. The new token
              takes effect on the next workflow run — there is nothing to update in
              n8n.
            </p>
          </div>
        )}
      </div>

      {/* Linked Workflow URL */}
      <div className="glass-card p-5 space-y-4">
        <div>
          <h3 className="text-sm font-semibold text-boss-text mb-1">Linked Workflow URL</h3>
          <p className="text-xs text-boss-text-muted">
            Paste the n8n workflow URL here for visibility (optional). This is not used for auth.
          </p>
        </div>

        <div className="flex gap-2">
          <input
            className="input-field flex-1"
            placeholder="https://your-n8n-instance.com/workflow/…"
            value={workflowUrl}
            onChange={(e) => setWorkflowUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveUrl();
            }}
            disabled={!canUpdate}
            aria-label="Linked workflow URL"
          />
          {canUpdate && (
            <button
              onClick={handleSaveUrl}
              disabled={savingUrl}
              className="btn-primary flex items-center gap-2 shrink-0"
            >
              <Save size={15} />
              {savingUrl ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>

        {settings?.linked_workflow_url && (
          <a
            href={settings.linked_workflow_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-violet-400 hover:text-violet-300 break-all block"
          >
            {settings.linked_workflow_url}
          </a>
        )}
      </div>

      {/* Rotate confirmation modal */}
      {rotateConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          {...backdrop}
        >
          <div className="bg-boss-surface border border-boss-border rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-base font-semibold text-boss-text mb-2">Regenerate Token?</h2>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 mb-5">
              <p className="text-sm text-amber-300 leading-relaxed">
                Rotating this token <strong>immediately invalidates the old one</strong>.
                Any dispatch already in flight with the previous token will be rejected
                and retried. There is nothing to update in n8n — BOSS supplies this
                token to the workflow on every run.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setRotateConfirmOpen(false)}
                className="btn-secondary flex-1"
                disabled={rotating}
              >
                Cancel
              </button>
              <button
                onClick={handleRotate}
                disabled={rotating}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                {rotating ? 'Rotating…' : 'Yes, Rotate Token'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
